import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface SEOMetadata {
  id: string;
  page_slug: string;
  meta_title: string | null;
  meta_description: string | null;
  canonical_url: string | null;
  og_title: string | null;
  og_description: string | null;
  og_image_url: string | null;
  og_type: string;
  twitter_card: string;
  robots: string;
  focus_keyword: string | null;
  schema_json: Record<string, unknown> | null;
  is_indexable: boolean;
  created_at: string;
  updated_at: string;
}

const DEFAULT_DESCRIPTION = "Magnetiq — Créez des landing pages et capturez des leads facilement.";

export function useSEO(slug: string, options?: { enabled?: boolean }) {
  const enabled = options?.enabled !== undefined ? options.enabled && !!slug : !!slug;
  return useQuery<SEOMetadata | null>({
    queryKey: ["seo-metadata", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("seo_metadata")
        .select("*")
        .eq("page_slug", slug)
        .maybeSingle();
      if (error) throw error;
      return data as SEOMetadata | null;
    },
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

function setMetaTag(name: string, content: string, attribute = "name") {
  let el = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attribute, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function removeMetaTag(name: string, attribute = "name") {
  const el = document.querySelector(`meta[${attribute}="${name}"]`);
  if (el) el.remove();
}

function setLinkTag(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function removeLinkTag(rel: string) {
  const el = document.querySelector(`link[rel="${rel}"]`);
  if (el) el.remove();
}

const SCHEMA_SCRIPT_ID = "seo-json-ld";

export function applySEOTags(meta: SEOMetadata | null, pageName?: string) {
  const title = meta?.meta_title || (pageName ? `${pageName} | Magnetiq` : "Magnetiq");
  const description = meta?.meta_description || DEFAULT_DESCRIPTION;

  document.title = title;
  setMetaTag("description", description);
  setMetaTag("robots", meta?.robots || "index, follow");

  // Open Graph
  setMetaTag("og:title", meta?.og_title || title, "property");
  setMetaTag("og:description", meta?.og_description || description, "property");
  setMetaTag("og:type", meta?.og_type || "website", "property");
  if (meta?.og_image_url) {
    setMetaTag("og:image", meta.og_image_url, "property");
  } else {
    removeMetaTag("og:image", "property");
  }

  // Twitter
  setMetaTag("twitter:card", meta?.twitter_card || "summary_large_image");
  setMetaTag("twitter:title", meta?.og_title || title);
  setMetaTag("twitter:description", meta?.og_description || description);
  if (meta?.og_image_url) {
    setMetaTag("twitter:image", meta.og_image_url);
  } else {
    removeMetaTag("twitter:image");
  }

  // Canonical
  if (meta?.canonical_url) {
    setLinkTag("canonical", meta.canonical_url);
  } else {
    removeLinkTag("canonical");
  }

  // JSON-LD
  let script = document.getElementById(SCHEMA_SCRIPT_ID) as HTMLScriptElement | null;
  if (meta?.schema_json) {
    if (!script) {
      script = document.createElement("script");
      script.id = SCHEMA_SCRIPT_ID;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(meta.schema_json);
  } else if (script) {
    script.remove();
  }
}
